import { Button, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import React, {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { FaCheck, FaPencilAlt, FaPlus } from "react-icons/fa";
import PopupModal from "../../PopupModal";
import { array, number, oneOf, oneOfType, string } from "prop-types";
import { displayNotification, getFormattedDate, sendAPIRequest } from "helpers";
import TextAreaField from "../../core/TextAreaField";
import AffirmationField from "../../core/AffirmationField";
import DatePickerCustom from "components/DatePickerCustom";
import {
    useCreateMultiFormDataMutation,
    useLazyGetOneMultiFormDataQuery,
    useUpdateMultiFormDataMutation,
} from "modules/pvs_activities/redux/service/formData";
import { FormDataContext } from "../../contexts/FormContext";
import CustomCKEditor from "../../CustomCKEditor";
import DropdownField from "../../core/DropdownField";
import dayjs from "dayjs";

const TableElementModifyDataentryModal = forwardRef((props, ref) => {
    const modalRef = useRef();
    const formDataContext = useContext(FormDataContext);
    const [trigger, result] = useLazyGetOneMultiFormDataQuery();
    const [createMultiFormData] = useCreateMultiFormDataMutation();
    const [updateMultiFormData] = useUpdateMultiFormDataMutation();

    const [data, setData] = useState({});

    useImperativeHandle(ref, () => {
        return {
            openModal: handleOpen,
        };
    });

    useEffect(() => {
        if (result?.status !== "uninitialized") {
            if (result?.isError) {
                displayNotification("error", "Error loading table row data");
                console.error({ error: result.error });
            }
            if (result.status === "fulfilled" && result.isSuccess && !result.isLoading) {
                const tmp = { ...data };
                for (const x in result.data) {
                    tmp[x] = result.data[x].content;
                    // tmp[x].list_id = result.data[x].list_id;
                    // tmp[x].list_element_id = result.data[x].list_element_id;
                }
                setData(tmp);
            }
        }
    }, [result]);

    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };
    const handleSave = useCallback(
        async (closeModal) => {
            const tmp = [];
            let isValid = true;
            let message = '';
            const columnMap = new Map(props.columns.map((col) => [col.id, col]));
            for (const k in data) {
                const column = columnMap.get(k);
                if (column && column.type === "email") {
                    if (!validateEmail(data[k])) {
                        isValid = false;
                        message = `Invalid email format for ${props.columns.find((col) => col.id === k).name}`;
                    };
                    const checkEmail = await sendAPIRequest('/api/pvsa/form-data/check-email', 'POST', {
                        "template_id": formDataContext.templateId,
                        "section_id": formDataContext.sectionId,
                        "movement_id": formDataContext.movementId,
                        "email": data[k],
                        "mode": props.mode,
                        "row_ref": props?.id ?? ''
                    });
                    const res = await checkEmail.json();
                    if (res.success === false && res?.status === 401) {
                        displayNotification('warning', res.message);
                        return;
                    };
                }

                if (column && column.is_required === 1 && (!data[k] || data[k] === "")) {
                    isValid = false;
                    message = `Please provide a value for ${column.name}. This field is required.`;
                    break;
                }
                tmp.push({
                    ...(Array.isArray(data[k])
                        ? { entries: data[k] }
                        : {
                            column_id: k,
                            content: typeof data[k] === "object" ? data[k]?.content : data[k],
                            list_id: typeof data[k] === "object" ? data[k]?.list_id : "",
                            list_element_id: typeof data[k] === "object" ? data[k]?.list_element_id : "",
                        }),
                });
            }
            if (isValid) {
                if (props.mode === "add") {
                    createMultiFormData({
                        template_id: formDataContext.templateId,
                        section_id: formDataContext.sectionId,
                        element_id: props.tableId,
                        movement_id: formDataContext.movementId,
                        row_ref: props.id ? props.id : "",
                        entries: tmp,
                    });
                    displayNotification("success", `Successfully added data`);
                } else if (props.mode === "edit") {
                    updateMultiFormData({
                        rowRef: props.id,
                        entries: tmp,
                    });
                    displayNotification("success", `Successfully updated data`);
                } else {
                    displayNotification("error", `Invalid mode while saving table row data`);
                }
                closeModal();
            } else {
                displayNotification("error", message);
            }
        },
        [data, props.tableId, props.mode, props.id, formDataContext, createMultiFormData, updateMultiFormData]
    );

    const resetDataState = useCallback(() => {
        if (props.columns.length > 0) {
            const temp = {};
            for (const x of props.columns) {
                if (x.type === "date") {
                    temp[x.id] = null;
                } else {
                    temp[x.id] = "";
                }
            }
            setData(temp);
        } else {
            setData({});
        }
    }, [props.columns]);

    const handleOpen = async () => {
        if (props.mode === 'edit') {
            await trigger(props.id).unwrap();
        }
        modalRef.current?.openModal();
    };

    useLayoutEffect(() => {
        resetDataState();
    }, [props.columns, resetDataState]);
    return (
        <PopupModal
            ref={modalRef}
            title={props.mode === "edit" ? "Edit Row" : "Add New"}
            triggers={() =>
                props.display === "menuitem" ? (
                    <MenuItem onClick={handleOpen}>
                        <ListItemIcon>{props.mode === "edit" ? <FaPencilAlt /> : <FaPlus />}</ListItemIcon>
                        <ListItemText>{props.mode === "edit" ? "Edit" : "Add"}</ListItemText>
                    </MenuItem>
                ) : props.display === "button" ? (
                    <Button variant={"contained"} color="primary" onClick={handleOpen}>
                        {props.mode === "edit" ? (
                            <>
                                <FaPencilAlt />
                                <span className="ms-1">Edit</span>
                            </>
                        ) : (
                            <>
                                <FaPlus />
                                <span className="ms-1">Add</span>
                            </>
                        )}
                    </Button>
                ) : (
                    ""
                )
            }
            footTriggers={({ closeModal }) => (
                <Button variant={"contained"} color="success" onClick={() => handleSave(closeModal)}>
                    <FaCheck />
                    <span className="ms-1">Save</span>
                </Button>
            )}
            onClose={resetDataState}
        >
            <div>
                {((props.mode === "edit" && result.isSuccess && !result.isLoading) || props.mode === "add") &&
                    (props.columns.length > 0
                        ? props.columns.map((r) => (
                            <div className="mb-2" key={r.id}>
                                {["text", "email", "number"].includes(r.type) && (
                                    <TextAreaField
                                        label={r.name}
                                        type={r.type}
                                        value={data[r.id] || ""}
                                        onChange={(val) => {
                                            setData({ ...data, [r.id]: val })
                                        }}
                                        onBlur={() => { }}
                                        error={false}
                                        required={r.required || false}
                                    />
                                )}
                                {["year", "percentage"].includes(r.type) && (
                                    <TextAreaField
                                        label={r.name}
                                        type={r.type}
                                        value={data[r.id] || ""}
                                        onChange={(val) => setData({ ...data, [r.id]: val })}
                                        onBlur={() => { }}
                                        error={false}
                                    />
                                )}
                                {["date"].includes(r.type) && (
                                    <DatePickerCustom
                                        label={r.name}
                                        sx={{ width: "100%" }}
                                        fullWidth
                                        className="form-control"
                                        onChange={(val) => {
                                            setData({
                                                ...data, [r.id]: getFormattedDate(val)
                                            })
                                        }}
                                        value={!["", "Invalid date"].includes(data[r.id]) ? dayjs(data[r.id]) : null}
                                        slotProps={{ textField: { size: "small" } }}
                                        inputFormat="dd-MMM-YY"
                                    />
                                )}
                                {["description"].includes(r.type) && (
                                    <CustomCKEditor
                                        height="200"
                                        value={data[r.id]}
                                        onChange={(val) => setData({ ...data, [r.id]: val })}
                                    />
                                )}
                                {["attachment"].includes(r.type) && (
                                    <input
                                        type="file"
                                        className="form-control"
                                        defaultValue={""}
                                        onChange={(e) =>
                                            setData({
                                                ...data,
                                                [r.id]: e.target.files[0],
                                            })
                                        }
                                    />
                                )}
                                {["affirmation"].includes(r.type) && (
                                    <AffirmationField
                                        label={r.name}
                                        isMinimal={false}
                                        hasNA={false}
                                        name={`affirmation_${r.id}`}
                                        value={data[r.id]}
                                        onChange={(e, val) => setData({ ...data, [r.id]: val })}
                                    />
                                )}
                                {["list"].includes(r.type) && (
                                    <DropdownField
                                        plain={true}
                                        label={r.name}
                                        canAdd={false}
                                        templateId={props.templateId}
                                        listID={r.list_id}
                                        multiple={false}
                                        value={data[r.id]}
                                        onChange={(val) => setData({ ...data, [r.id]: val })}
                                    />
                                )}
                            </div>
                        ))
                        : "No Columns Available")}
            </div>
        </PopupModal>
    );
});

TableElementModifyDataentryModal.propTypes = {
    mode: oneOf(["add", "edit"]).isRequired,
    display: oneOf(["button", "menuitem", "none"]).isRequired,
    id: oneOfType([number, string]),
    templateId: string.isRequired,
    tableId: string.isRequired,
    columns: array.isRequired,
};

TableElementModifyDataentryModal.defaultProps = {
    mode: "add",
    display: "button",
    id: null,
    templateId: "",
    tableId: "",
    columns: [],
};

TableElementModifyDataentryModal.displayName = "TableElementModifyDataentryModal";

export default TableElementModifyDataentryModal;
